import { useCallback, useState } from 'react';
import enhancedExportService from '../../services/enhancedExportService';
import pptxExportService from '../../services/pptxExportService';
import { exportBaseName } from './documentMeta';

/**
 * Export a document (Word, PDF, HTML or PowerPoint) and track which one is exporting.
 * @returns {{exporting: {id: string, type: string}|null, exportDocument: function(object, string, string): Promise<boolean>}}
 *   `exportDocument(document, type, projectName)` resolves true on success.
 */
export default function useDocumentExport() {
  const [exporting, setExporting] = useState(null);

  const exportDocument = useCallback(async (document, type, projectName = '') => {
    if (!document?.content) return false;
    const base = exportBaseName(document.name);
    setExporting({ id: document.id, type });
    try {
      switch (type) {
        case 'word':
          await enhancedExportService.exportToWord(document.content, `${base}.docx`, projectName);
          break;
        case 'pdf':
          await enhancedExportService.exportToPDF(document.content, `${base}.pdf`, projectName);
          break;
        case 'html':
          await enhancedExportService.exportToHTML(document.content, `${base}.html`, projectName);
          break;
        case 'pptx':
          await pptxExportService.exportToPptx(document.content, `${base}.pptx`, projectName);
          break;
        default:
          throw new Error('Unknown export type');
      }
      return true;
    } catch (error) {
      console.error(`${String(type).toUpperCase()} export failed:`, error);
      return false;
    } finally {
      setExporting(null);
    }
  }, []);

  return { exporting, exportDocument };
}
